import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { api } from '../lib/api';

const AppCtx = createContext(null);

export function AppProvider({ children }) {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [activeChild, setActiveChild] = useState(() => {
    try { return JSON.parse(localStorage.getItem('activeChild') || 'null'); } catch { return null; }
  });
  const [uiLang, setUiLang] = useState(() => localStorage.getItem('uiLang') || 'en');
  // Iter-51.35 — session-scoped child PIN unlocks. Once a child enters
  // their profile PIN correctly (or the parent taps "let me in" via the
  // Parent PIN override), we store the child_id in sessionStorage so
  // subsequent profile switches during this browser session skip the
  // PIN gate. Cleared on logout / new tab.
  const [childUnlocks, setChildUnlocks] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('childUnlocks') || '{}'); }
    catch { return {}; }
  });
  // Iter-49: billing state — populated after auth so RequireAuth can route
  // expired users to /trial-ended without hitting the login flow again.
  const [billing, setBilling] = useState(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const billingReq = useRef(0);

  const checkAuth = useCallback(async () => {
    if (typeof window !== 'undefined' && window.location.hash?.includes('session_id=')) {
      setAuthLoading(false);
      return;
    }
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setAuthLoading(false);
    }
  }, []);

  // Iter-52.4 — light refresh (no loading spinner flash) after
  // mutations that alter user fields the SPA reads — currently used
  // by the WelcomeVideoModal after `/users/mark-welcome-seen`.
  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
    } catch { /* keep existing user on transient failure */ }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  // Fetch billing whenever the user changes. Debounced by ref so parallel
  // race conditions (e.g. checkAuth followed by a rapid re-render) don't
  // paint stale plan data.
  const refreshBilling = useCallback(async () => {
    const ticket = ++billingReq.current;
    setBillingLoading(true);
    try {
      const { data } = await api.get('/billing/status');
      if (ticket === billingReq.current) setBilling(data);
    } catch {
      if (ticket === billingReq.current) setBilling(null);
    } finally {
      if (ticket === billingReq.current) setBillingLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) refreshBilling(); else setBilling(null);
  }, [user, refreshBilling]);

  const pickChild = (child) => {
    setActiveChild(child);
    localStorage.setItem('activeChild', JSON.stringify(child));
  };

  const updateActiveChild = (patch) => {
    setActiveChild(prev => {
      if (!prev) return prev;
      const next = { ...prev, ...patch };
      localStorage.setItem('activeChild', JSON.stringify(next));
      return next;
    });
  };

  const clearChild = () => {
    setActiveChild(null);
    localStorage.removeItem('activeChild');
  };

  const changeUiLang = (l) => {
    setUiLang(l);
    localStorage.setItem('uiLang', l);
  };

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch {}
    setUser(null);
    setBilling(null);
    clearChild();
    // Iter-51.35 — wipe child PIN unlocks on logout so a shared device
    // doesn't leak a previously-unlocked profile to the next user.
    setChildUnlocks({});
    try { sessionStorage.removeItem('childUnlocks'); } catch {}
    // Iter-52.1 — defensively wipe the legacy AI-disclaimer dismissal
    // key. The banner is now permanent (see AiDisclaimerBanner.jsx)
    // and no longer reads this flag, but a returning agent might
    // reintroduce a similar pattern — clearing it on logout keeps
    // us safe against dismissal state leaking across auth sessions
    // in the same tab. sessionStorage is scoped to the tab, not
    // to the auth session, so any UI dismissal state MUST be
    // cleared here explicitly.
    try { sessionStorage.removeItem('readbuddy_ai_disclaimer_dismissed_v1'); } catch {}
  };

  // Iter-51.35 — child PIN unlock helpers exposed via context.
  const isChildUnlocked = useCallback((child_id) => (
    !!(child_id && childUnlocks[child_id])
  ), [childUnlocks]);

  const markChildUnlocked = useCallback((child_id, token) => {
    setChildUnlocks(prev => {
      const next = { ...prev, [child_id]: token || true };
      try { sessionStorage.setItem('childUnlocks', JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);

  // Iter-51.42 — active-quiz navigation guard. Quiz.jsx registers a
  // handler while a round is in play so the bottom-nav (and any
  // other in-app link) can ask permission before yanking the child
  // off the quiz screen. Handler receives the intended target path
  // and returns TRUE to block the navigation (Quiz's own confirm
  // modal will then decide whether to proceed) or FALSE to let it
  // through. Cleared on unmount.
  const [navBlocker, setNavBlockerState] = useState(null);
  const setNavBlocker = useCallback((fn) => {
    setNavBlockerState(() => fn);
  }, []);

  return (
    <AppCtx.Provider value={{
      user, setUser, authLoading,
      activeChild, pickChild, updateActiveChild, clearChild,
      uiLang, changeUiLang,
      logout, checkAuth, refreshUser,
      billing, billingLoading, refreshBilling,
      isChildUnlocked, markChildUnlocked,
      navBlocker, setNavBlocker,
    }}>
      {children}
    </AppCtx.Provider>
  );
}

export const useApp = () => useContext(AppCtx);
